import { useRef, useState, useMemo, useEffect, type ChangeEvent } from "react";
import {
  Box,
  Button,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Tabs,
  Tab,
  TabList,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { Search, Plus, Download } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import {
  DataTable,
  type DataTableRef,
  type DataTableOptions,
} from "@/components/tables";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import { useAccessCheck } from "@/hooks/useAccess";
import { exportToCSV } from "@/lib/utils/csvUtils";
import { apiClient } from "@/lib/api/client";
import { useThemeColors } from "@/hooks/useThemeColors";

type TabValue = "all" | "trash";

export default function RolesPage() {
  const colors = useThemeColors();
  const navigate = useNavigate();
  const tableRef = useRef<DataTableRef>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const { showError, showSuccess, showWarning } = useToastNotification();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [activeTab, setActiveTab] = useState<TabValue>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [totalTrash, setTotalTrash] = useState(0);
  const [bulkAction, setBulkAction] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const access = useAccessCheck();

  const statusFilterOptions = useMemo(
    () => [
      { value: "Active", label: "Active" },
      { value: "Inactive", label: "Inactive" },
    ],
    [],
  );

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        name: {
          name: "Name",
          withSort: true,
          maxWidth: "200px",
        },
        active: {
          name: "Status",
          withSort: true,
          withFilter: true,
          filterOptions: statusFilterOptions,
          badge: {
            Active: "green",
            Inactive: "yellow",
          },
          badgeLabels: {
            Active: "Active",
            Inactive: "Inactive",
          },
        },
        updated_at: {
          name: "Updated At",
          withSort: true,
          maxWidth: "150px",
        },
      },
      dataSource:
        activeTab === "trash"
          ? "/user-management/archived/roles"
          : "/user-management/roles",
      primaryKey: "id",
      softDelete: true,
      displayInModal: false,
      noRecordText: "No roles found",
    }),
    [activeTab, statusFilterOptions],
  );

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access],
  );

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery]);

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all);
      setTotalTrash(trashed);
    },
    [],
  );

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? "all" : "trash");
    setSearchQuery("");
    if (searchRef.current) {
      searchRef.current.value = "";
    }
    tableRef.current?.clearPage();
  };

  const handleSearch = () => {
    const query = searchRef.current?.value || "";
    setSearchQuery(query);
    tableRef.current?.applyFilter("search", query);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning("Please select an action first");
      return;
    }
    const selectedIds = tableRef.current?.getSelectedRows() || [];
    if (selectedIds.length === 0) {
      showWarning("Please select at least one item");
      return;
    }

    if (bulkAction === "delete" && activeTab === "trash") {
      setModalMessage(
        "You are about to permanently delete these items from your site. This action cannot be undone.",
      );
    } else {
      setModalMessage("Are you sure to apply this change?");
    }
    onOpen();
  };

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || [];
    if (selectedIds.length === 0) return;

    setIsBulkProcessing(true);
    try {
      let url = "";
      let payload: any = { ids: selectedIds };
      let successMessage = "Action completed successfully";

      if (bulkAction === "restore") {
        url = "/user-management/roles/bulk/restore";
        successMessage = "Roles restored successfully";
      } else if (bulkAction === "delete") {
        url =
          activeTab === "trash"
            ? "/user-management/roles/bulk/force-delete"
            : "/user-management/roles/bulk/delete";
        successMessage =
          activeTab === "trash"
            ? "Roles permanently deleted successfully"
            : "Roles moved to trash successfully";
      }

      await apiClient.post(url, payload);
      showSuccess("Success", successMessage);
      tableRef.current?.reload();
      setBulkAction("");
      onClose();
    } catch (error: any) {
      showError(error);
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      await exportToCSV({
        endpoint: "/user-management/roles",
        selectedIds: [],
        activeTab,
        searchQuery: "",
        csvHeaders: [
          "ID",
          "Name",
          "Active",
          "Created At",
          "Updated At",
          "Deleted At",
        ],
        dataMapper: (role: any) => [
          role.id || "",
          role.name || "",
          role.active === "Active" ? "Yes" : "No",
          role.created_at || "",
          role.updated_at || "",
          role.deleted_at || "",
        ],
        filename: `roles_export_all${activeTab === "trash" ? "_trash" : ""}_${new Date().toISOString().split("T")[0]}.csv`,
        showError,
        showSuccess,
        showWarning,
      });
    } catch (error: any) {
      showError(error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <AdminLayout>
      <Box bg="#FEFCF8" minH="100vh" p={4}>
        <VStack spacing={4} align="stretch" flex="1" minH="100vh">
          {/* Header Section */}
          <Box>
            <Heading size="md" mb={1}>
              Roles
            </Heading>
            <Text fontSize="sm" color={colors.text}>
              Manage user roles and permissions
            </Text>
          </Box>

          {/* Tabs and Action Buttons Section */}
          <Box>
            <HStack
              justify="space-between"
              align="flex-end"
              spacing={4}
              width="100%"
            >
              <Tabs
                index={activeTab === "all" ? 0 : 1}
                onChange={handleTabChange}
              >
                <TabList borderBottom="none">
                  <Tab color="#DC3611">
                    All{" "}
                    <Badge ml={2} color="#DC3611">
                      {totalRows}
                    </Badge>
                  </Tab>
                  {totalTrash > 0 && (
                    <Tab color="#DC3611">
                      Trash{" "}
                      <Badge ml={2} color="#DC3611">
                        {totalTrash}
                      </Badge>
                    </Tab>
                  )}
                </TabList>
              </Tabs>
              {/* Custom Buttons */}
              <HStack spacing={2}>
                <Button
                  onClick={handleExportData}
                  leftIcon={<Download size={16} />}
                  size="sm"
                  variant="outline"
                  borderColor="gray.300"
                  bg="white"
                  color="gray.700"
                  _hover={{ bg: "gray.100" }}
                  isLoading={isExporting}
                  loadingText="Exporting..."
                >
                  Export
                </Button>
                <Button
                  as={Link}
                  to="/user-management/roles/create"
                  leftIcon={<Plus size={16} />}
                  size="sm"
                  bg="#DC3611"
                  color="white"
                  _hover={{ bg: "#b32d0e" }}
                >
                  Create New Role
                </Button>
              </HStack>
            </HStack>
            <Box borderBottom="1px" borderColor={colors.border} mt={3} />
          </Box>

          {/* Search and Bulk Actions Section */}
          <HStack spacing={4} flexWrap="wrap" py={2}>
            {access.can_delete && (
              <HStack spacing={2} minW="250px">
                <Select
                  placeholder="Bulk actions"
                  size="sm"
                  bg="white"
                  value={bulkAction}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                    e.stopPropagation();
                    setBulkAction(e.target.value);
                  }}
                >
                  {activeTab === "trash" && (
                    <option value="restore">Restore</option>
                  )}
                  {activeTab === "trash" && (
                    <option value="delete">Delete Permanently</option>
                  )}
                  {activeTab === "all" && (
                    <option value="delete">Delete</option>
                  )}
                </Select>
                <Button
                  size="sm"
                  color="white"
                  bg="#DC3611"
                  _hover={{ bg: "#bf3c1fff" }}
                  onClick={handleBulkActionClick}
                >
                  Apply
                </Button>
              </HStack>
            )}

            <Box flex="0 1 auto" minW="250px" maxW="400px">
              <InputGroup size="sm">
                <Input
                  ref={searchRef}
                  placeholder="Search by name..."
                  onKeyDown={handleKeyDown}
                  bg="white"
                  defaultValue={searchQuery}
                  pr="2.5rem"
                />
                <InputRightElement width="2.5rem">
                  <IconButton
                    aria-label="Search"
                    icon={<Search size={16} />}
                    size="sm"
                    h="1.75rem"
                    w="1.75rem"
                    onClick={handleSearch}
                    variant="ghost"
                  />
                </InputRightElement>
              </InputGroup>
            </Box>
          </HStack>

          {/* Table Section  */}
          <Box
            flex="1"
            overflow="auto"
            display="flex"
            flexDirection="column"
            minH={0}
          >
            <DataTable
              ref={tableRef}
              options={tableOptions}
              params={tableParams}
              access={permissions}
              setSubSub={showSubSubCallback}
              onOpenModal={(id) => navigate(`/user-management/roles/${id}`)}
              filterOptions={{
                active: statusFilterOptions,
              }}
            />
          </Box>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onClose}
              isDisabled={isBulkProcessing}
            >
              Cancel
            </Button>
            <Button
              bg="#DC3611"
              color="white"
              _hover={{ bg: "#b32d0e" }}
              onClick={handleConfirmBulkAction}
              isLoading={isBulkProcessing}
              loadingText="Processing..."
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  );
}
